<!--
  Bloco inline de bloqueio por horas consecutivas na unidade. Mesmo padrão visual do
  `schedule-conflict-alert` (HandoverConfirmationModal), mas com payload rico: motivo,
  plantões que somam na contagem e quando o médico poderá voltar.
  Reutilizado pelos modais do painel e pela aprovação admin de trocas.
-->
<template>
  <div
    v-if="violations.length > 0"
    data-testid="consecutive-hours-block-alert"
    class="flex flex-col gap-3 rounded-lg p-3 bg-[#fdecec] border border-[#da1e28] text-left"
  >
    <div class="flex flex-wrap items-center gap-2">
      <p class="text-sm text-[#16161d] leading-[18px]">
        <span class="font-bold">Limite de horas consecutivas:</span>
        {{ headerMessage }}
      </p>
      <LabelBadge v-if="showBlockBadge" label="IMPEDE" color="red" size="small" :icon="null" />
    </div>

    <div
      v-for="(violation, index) in violations"
      :key="`${violation.doctorId}-${index}`"
      data-testid="consecutive-hours-violation-item"
      class="flex flex-col gap-1"
    >
      <p class="text-sm text-[#16161d] leading-[18px]">
        {{ formatViolationHeadline(violation) }}
      </p>

      <template v-if="violation.contributingStations.length > 0">
        <p class="text-sm font-bold text-[#16161d] leading-[18px]">
          Plantões que somam na contagem:
        </p>
        <ul class="flex flex-col gap-1">
          <li
            v-for="(station, stationIndex) in violation.contributingStations"
            :key="stationIndex"
            class="text-sm text-[#16161d] leading-[18px]"
          >
            • {{ formatContributingStation(station) }}
          </li>
        </ul>
      </template>

      <p
        v-if="formatAvailableAt(violation)"
        class="text-sm text-[#16161d] leading-[18px] font-bold"
      >
        {{ formatAvailableAt(violation) }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  formatViolationHeadline,
  formatContributingStation,
  formatAvailableAt
} from '@/utils/consecutiveHoursErrors'
import type { ConsecutiveHoursViolation } from '@/Network/Types/Responses/ConsecutiveHoursResponses'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'

const props = withDefaults(
  defineProps<{
    violations: ConsecutiveHoursViolation[]
    /** Operação em massa (ex.: alocação em lote): mensagem de "nada foi alterado" mesmo com 1 violação. */
    bulk?: boolean
    /**
     * Etiqueta "IMPEDE" ao lado do título — opt-in (default false) para não alterar
     * os modais de vaga (management.doctor.modal, create/update.station.modal) que
     * reaproveitam este componente. Só o modal de aprovação de troca liga.
     */
    showBlockBadge?: boolean
  }>(),
  {
    bulk: false,
    showBlockBadge: false
  }
)

// Operação em massa é tudo-ou-nada: deixa explícito que nada foi aplicado.
const headerMessage = computed(() =>
  props.bulk || props.violations.length > 1
    ? 'esta operação não pode ser concluída. Nenhum plantão foi alterado.'
    : 'esta alocação não pode ser concluída.'
)
</script>
